import {kasseClient} from '@/js/helpers/backend-clients';
import {displayAxiosError, displayErrorMessage, hideLoadingOverlay, showLoadingOverlay} from '@/js/helpers/utils';
import router from '@/router';
import context from '@/js/context';

const defaultState = () => {
  let persistentWarenkorb = window.sessionStorage.getItem('warenkorb');
  let persistProduktauswahl = window.sessionStorage.getItem('produktauswahl');
  return {
    warenkorb: persistentWarenkorb ? JSON.parse(persistentWarenkorb) : [],
    produktdaten: [],
    panel: [0],
    brutto: 0,
    netto: 0,
    steuer: 0,
    mwst: '',
    adresszusatz: '',
    produktvorauswahl: persistProduktauswahl ? JSON.parse(persistProduktauswahl) : [],
    agbAkzeptiert: false,
  };
};
const state = defaultState();

const mutations = {
  UPDATE_WARENKORB_PRODUKTE(state, obj) {
    state.warenkorb = obj;
    window.sessionStorage.setItem('warenkorb', JSON.stringify(obj));
  },
  UPDATE_WOHNUNGSNUMMER(state, obj) {
    state.adresszusatz = obj;
  },
  SET_PRODUKTDATEN(state, data) {
    state.produktdaten = data;
  },
  SET_BRUTTO(state, data) {
    state.brutto = data;
  },
  SET_NETTO(state, data) {
    state.netto = data;
  },
  SET_STEUER(state, data) {
    state.steuer = data;
  },
  SET_MWST(state, data) {
    state.mwst = data;
  },
  SET_PRODUKT_VORAUSWAHL(state, data) {
    state.produktvorauswahl = data;
  },
  RESET_STATE(state) {
    Object.assign(state, defaultState());
  },
  SET_VORSCHAUKARTE_URL(state, data) {
    state.warenkorb[data.idx].vorschaukarteUrl = data.karte;
  },
  SET_MAP_CENTER(state, data) {
    state.warenkorb[data.idx].attribute.find(a => a.name.endsWith('_mapcenter')).wert =
      data.mapcenter;
  },
  SET_PANEL(state, val) {
    state.panel = val;
  },
  SET_AGB_AKZEPTIERT(state, val) {
    state.agbAkzeptiert = val;
  },
};

const actions = {
  starteRNDGutachten({commit, dispatch}, {objektart, objektbesichtigung}) {
    showLoadingOverlay();
    kasseClient
      .erstelleRndGutachten({objektart, objektbesichtigung})
      .then(({data}) => {
        commit('UPDATE_WARENKORB_PRODUKTE', [data]);
        data.attribute.forEach(attribut => {
          if (attribut.name.endsWith('_objektart')) {
            dispatch(`product/setObjektart`, attribut.wert, {root: true});
          }
          if (attribut.name.endsWith('_objektbesichtigung')) {
            dispatch(`product/setObjektbesichtigung`, attribut.wert, {root: true});
          }
          dispatch('setVorname', {objektbesichtigung, attribut});
          dispatch('setNachname', {objektbesichtigung, attribut});
        });
        dispatch('product/setObjektart', data.attribute[0].wert, {
          root: true,
        });
        dispatch('setDefaultRechnungName1', {objektbesichtigung, name1: data.defaultRechnung.name1});
        dispatch('setDefaultRechnungName2', {objektbesichtigung, name2: data.defaultRechnung.name2});
        dispatch('setDefaultRechnungPlz', {objektbesichtigung, plz: data.defaultRechnung.plz});
        dispatch('setDefaultRechnungOrt', {objektbesichtigung, ort: data.defaultRechnung.ort});
        dispatch('setDefaultRechnungStr', {objektbesichtigung, str: data.defaultRechnung.str});
        dispatch('setDefaultRechnungHnr', {objektbesichtigung, hnr: data.defaultRechnung.hnr});
        router.push('/konfiguration/ndg');
      })
      .catch(error => {
        displayAxiosError(error);
      })
      .finally(() => {
        hideLoadingOverlay();
      });
  },
  starteKpaGutachten({commit, dispatch}) {
    showLoadingOverlay();
    kasseClient
      .erstelleKpaGutachten()
      .then(({data}) => {
        commit('UPDATE_WARENKORB_PRODUKTE', [data]);
        data.attribute.forEach(attribut => {
          if (attribut.name.endsWith('_kontakt_name1')) {
            dispatch('kaufpreisaufteilung/setAnsprechpartnerName1', attribut['wert'], {root: true,});
          }
          if (attribut.name.endsWith('_kontakt_name2')) {
            dispatch('kaufpreisaufteilung/setAnsprechpartnerName2', attribut['wert'], {root: true,});
          }
        });
        dispatch('kaufpreisaufteilung/setDefaultRechnungName1', data.defaultRechnung.name1, {root: true});
        dispatch('kaufpreisaufteilung/setDefaultRechnungName2', data.defaultRechnung.name2, {root: true});
        dispatch('kaufpreisaufteilung/setDefaultRechnungPlz', data.defaultRechnung.plz, {root: true});
        dispatch('kaufpreisaufteilung/setDefaultRechnungOrt', data.defaultRechnung.ort, {root: true});
        dispatch('kaufpreisaufteilung/setDefaultRechnungStr', data.defaultRechnung.str, {root: true});
        dispatch('kaufpreisaufteilung/setDefaultRechnungHnr', data.defaultRechnung.hnr, {root: true});
        router.push('/konfiguration/kpa');
      })
      .catch(error => {
        displayAxiosError(error);
      })
      .finally(() => {
        hideLoadingOverlay();
      });
  },
  setVorname({dispatch}, {objektbesichtigung, attribut}) {
    if (attribut.name.endsWith('_kontakt_name1')) {
      switch (objektbesichtigung) {
        case context.BESICHTIGUNG:
          dispatch('objektbesichtigung/setAnsprechpartnerVorname', attribut.wert, {
            root: true,
          });
          break;
        case context.KEINE:
          dispatch('gutachten/setAnsprechpartnerVorname', attribut.wert, {
            root: true,
          });
          break;
      }
    }
  },
  setNachname({dispatch}, {objektbesichtigung, attribut}) {
    if (attribut.name.endsWith('_kontakt_name2')) {
      switch (objektbesichtigung) {
        case context.BESICHTIGUNG:
          dispatch('objektbesichtigung/setAnsprechpartnerNachname', attribut.wert, {
            root: true,
          });
          break;
        case context.KEINE:
          dispatch('gutachten/setAnsprechpartnerNachname', attribut.wert, {
            root: true,
          });
          break;
      }
    }
  },
  setDefaultRechnungName1({dispatch}, {objektbesichtigung, name1}) {
    switch (objektbesichtigung) {
      case context.BESICHTIGUNG:
        dispatch('objektbesichtigung/setDefaultRechnungName1', name1, {
          root: true,
        });
        break;
      case context.KEINE:
        dispatch('gutachten/setDefaultRechnungName1', name1, {
          root: true,
        });
        break;
    }
  },
  setDefaultRechnungName2({dispatch}, {objektbesichtigung, name2}) {
    switch (objektbesichtigung) {
      case context.BESICHTIGUNG:
        dispatch('objektbesichtigung/setDefaultRechnungName2', name2, {
          root: true,
        });
        break;
      case context.KEINE:
        dispatch('gutachten/setDefaultRechnungName2', name2, {
          root: true,
        });
        break;
    }
  },
  setDefaultRechnungPlz({dispatch}, {objektbesichtigung, plz}) {
    switch (objektbesichtigung) {
      case context.BESICHTIGUNG:
        dispatch('objektbesichtigung/setDefaultRechnungPlz', plz, {
          root: true,
        });
        break;
      case context.KEINE:
        dispatch('gutachten/setDefaultRechnungPlz', plz, {
          root: true,
        });
        break;
    }
  },
  setDefaultRechnungOrt({dispatch}, {objektbesichtigung, ort}) {
    switch (objektbesichtigung) {
      case context.BESICHTIGUNG:
        dispatch('objektbesichtigung/setDefaultRechnungOrt', ort, {
          root: true,
        });
        break;
      case context.KEINE:
        dispatch('gutachten/setDefaultRechnungOrt', ort, {
          root: true,
        });
        break;
      }
  },
  setDefaultRechnungStr({dispatch}, {objektbesichtigung, str}) {
    switch (objektbesichtigung) {
      case context.BESICHTIGUNG:
        dispatch('objektbesichtigung/setDefaultRechnungStr', str, {
          root: true,
        });
        break;
      case context.KEINE:
        dispatch('gutachten/setDefaultRechnungStr', str, {
          root: true,
        });
        break;
    }
  },
  setDefaultRechnungHnr({dispatch}, {objektbesichtigung, hnr}) {
    switch (objektbesichtigung) {
      case context.BESICHTIGUNG:
        dispatch('objektbesichtigung/setDefaultRechnungHnr', hnr, {
          root: true,
        });
        break;
      case context.KEINE:
        dispatch('gutachten/setDefaultRechnungHnr', hnr, {
          root: true,
        });
        break;
    }
  },
  starteProdukte({state, commit}, indizes) {
    if (!indizes || indizes.length < 1) {
      displayErrorMessage('Keine Produkte ausgewählt');
      return;
    }
    let data = indizes.map(val => state.produktvorauswahl[val]);
    if (data && data.length > 0) {
      data.forEach(d => {
        d.attribute.forEach(a => (a.wert = a.type === 'checkbox' ? false : a.wert));
        d.checked = true;
      });
      commit('UPDATE_WARENKORB_PRODUKTE', data);
      window.sessionStorage.setItem('produktauswahl', JSON.stringify(state.produktvorauswahl));
      router.push('/konfiguration/ndg');
    } else {
      displayErrorMessage('Keine Produkte ausgewählt');
    }
  },
  ueberpruefeWarenkorbProdukt({state}, idx) {
    window.sessionStorage.setItem('warenkorb', JSON.stringify(state.warenkorb));
    return kasseClient.checkProduct(state.warenkorb[idx]);
  },

  ueberpruefeWarenkorb({state}, errorCallback) {
    window.sessionStorage.setItem('warenkorb', JSON.stringify(state.warenkorb));
    showLoadingOverlay();
    kasseClient
      .checkOut(state.warenkorb)
      .then(({data}) => {
        if (data === 'immoWert') {
          window.sessionStorage.removeItem('produktauswahl');
          window.sessionStorage.removeItem('warenkorb');
          router.push('/meine-gutachten');
        } else {
          router.push('/warenkorb');
        }
      })
      .catch(errorCallback)
      .finally(() => {
        hideLoadingOverlay();
      });
  },
  ueberpruefeRnd({state, dispatch, rootGetters, commit}, {errorCallback}) {
    window.sessionStorage.setItem('warenkorb', JSON.stringify(state.warenkorb));
    showLoadingOverlay();
    const data = dispatch('product/getData', null, {root: true,});
    data.then(value => {
      commit('kasse/UPDATE_WOHNUNGSNUMMER', value.allgemeineAngaben.wohnungsnummer, { root: true });
      return value;
    }).then(value => kasseClient.checkOutRnd(value))
      .then(_ => {
        router.push('/warenkorb');
      })
      .catch(errorCallback)
      .finally(() => {
        hideLoadingOverlay();
      });
  },
  ueberpruefeKpa({state, dispatch, rootGetters, commit}, {errorCallback}) {
    window.sessionStorage.setItem('warenkorb', JSON.stringify(state.warenkorb));
    showLoadingOverlay();
    const data = dispatch('product/getDataKpa', null, {root: true,});
    data.then(value => {
      commit('kasse/UPDATE_WOHNUNGSNUMMER', value.allgemeineAngaben.wohnungsnummer, { root: true });
      return value;
    }).then(value => kasseClient.checkOutKpa(value))
      .then(_ => {
        router.push('/warenkorb');
      })
      .catch(errorCallback)
      .finally(() => {
        hideLoadingOverlay();
      });
  },
  produkteKaufen(errorCallback) {
    showLoadingOverlay();
    kasseClient
      .kaufen()
      .then(() => {
        window.sessionStorage.removeItem('produktauswahl');
        window.sessionStorage.removeItem('warenkorb');
        router.push('/meine-gutachten');
      })
      .catch(errorCallback)
      .finally(() => hideLoadingOverlay());
  },
  loescheProdukt({dispatch, commit}, dtype) {
    showLoadingOverlay();
    kasseClient
      .loescheProdukt(dtype)
      .then(({data}) => {
        if (data.produkte.length < 1) {
          dispatch('routeToConfigByDtype', dtype);
        } else {
          commit('SET_PRODUKTDATEN', data.produkte);
          commit('SET_BRUTTO', data.total);
          commit('SET_NETTO', data.netto);
          commit('SET_STEUER', data.steuer);
          commit('SET_MWST', data.steuersatz);
        }
      })
      .finally(() => hideLoadingOverlay());
  },
  loescheWarenkorb({commit}) {
    showLoadingOverlay();
    kasseClient
      .loescheWarenkorb()
      .then(() => {
        commit('SET_PRODUKTDATEN', []);
        commit('SET_BRUTTO', 0);
        commit('SET_NETTO', 0);
        commit('SET_STEUER', 0);
        commit('SET_MWST', '');
      })
      .finally(() => hideLoadingOverlay());
  },
  loadKasseData({commit}) {
    showLoadingOverlay();
    kasseClient
      .ladeKasse()
      .then(({data}) => {
        commit('SET_PRODUKTDATEN', data.produkte);
        commit('SET_BRUTTO', data.total);
        commit('SET_NETTO', data.netto);
        commit('SET_STEUER', data.steuer);
        commit('SET_MWST', data.steuersatz);
      })
      .finally(() => {
        hideLoadingOverlay();
      });
  },
  reset({dispatch}) {
    window.sessionStorage.removeItem('produktauswahl');
    dispatch('resetWarenkorb');
  },
  routeToConfigByDtype(_, dtype) {
    switch (dtype) {
      case 'kpa':
        router.push('/konfiguration/kpa');
        break;
      case 'ndgetw':
      case 'ndgmfh':
      case 'ndgefh':
      case 'ndgwgh':
      case 'ndgetwob':
      case 'ndgmfhob':
      case 'ndgefhob':
      case 'ndgwghob':
      case 'ndggkob':
      case 'ndggmob':
      case 'ndgggob':
        router.push('/konfiguration/ndg');
        break;
      default:
        throw new Error(`Dtype ${dtype} ist unbekannt!`);
    }
  },
  resetWarenkorb({commit, state}) {
    window.sessionStorage.removeItem('warenkorb');
    let arr = state.panel;
    commit('RESET_STATE');
    commit('SET_PANEL', arr);
  },
  vorschaukarteAnpassen({state, commit}, data) {
    showLoadingOverlay();
    kasseClient
      .vorschaukarte(data.request, state.warenkorb[data.idx].dtype)
      .then(resp => {
        commit('SET_VORSCHAUKARTE_URL', {
          idx: data.idx,
          karte: resp.data.vorschaukarteUrl,
        });
        commit('SET_MAP_CENTER', {
          idx: data.idx,
          mapcenter: resp.data.mapcenter,
        });
      })
      .catch(error => displayAxiosError(error))
      .finally(() => hideLoadingOverlay());
  },
  attributVorauswahl({state}, data) {
    let attributID = data.name.slice(data.name.indexOf('_'));
    state.warenkorb.forEach(w => {
      w.attribute.forEach(a => {
        if ((!a.wert || a.wert === '' || data.idx === 0) && a.name.endsWith(attributID)) {
          a.wert = data.wert;
        }
      });
    });
    window.sessionStorage.setItem('warenkorb', JSON.stringify(state.warenkorb));
  },
  setPanel({commit}, val) {
    commit('SET_PANEL', val);
  },
  setAgbAkzeptiert({commit}, val) {
    commit('SET_AGB_AKZEPTIERT', val);
  },
};

const getters = {
  warenkorb: () => state.warenkorb,
  produktdaten: () => state.produktdaten,
  produktauswahl: () => {
    let data = {};
    for (let i = 0; i < state.produktvorauswahl.length; i++) {
      let p = state.produktvorauswahl[i];
      if (!data[p.kategorie]) {
        data[p.kategorie.trim()] = [];
      }
      data[p.kategorie.trim()].push({
        name: p.anzeigename,
        dtype: p.dtype,
        preis: p.anzeigepreis,
        beschreibung: p.beschreibung,
        index: i,
        checked: p.checked,
      });
    }
    const orderedData = {};
    return Object.assign(orderedData, data);
  },
  brutto: () => state.brutto,
  netto: () => state.netto,
  steuer: () => state.steuer,
  mwst: () => state.mwst,
  panel: () => state.panel,
  agbAkzeptiert: () => state.agbAkzeptiert,
  adresszusatz: () => {
    return state.adresszusatz ? ('WhgNr: ' + state.adresszusatz) : '';
  },
};

const kasseModule = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};

export default kasseModule;
